/**
 * Trust Center Preview - Internal view of Public Trust Center
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { ExternalLink, X, Upload, FileCheck, Loader2, Shield, Eye, Settings, Lock, Zap } from 'lucide-react';
import { Card, Badge, CATEGORIES } from '../components/ui-custom/ComplianceUI';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function TrustCenterPreview() {
  const [showNDAModal, setShowNDAModal] = useState(false);
  const [showQuestionnaireModal, setShowQuestionnaireModal] = useState(false);
  const [ndaForm, setNdaForm] = useState({ contact_name: '', contact_email: '', company_name: '', reason: '' });
  const [questionnaireFile, setQuestionnaireFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  const handleNDASubmit = async () => {
    if (!ndaForm.contact_email || !ndaForm.company_name) {
      toast.error('Please fill in all required fields');
      return;
    }
    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/trust-center/nda-request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(ndaForm),
      });
      if (response.ok) {
        toast.success('NDA request submitted! We will contact you within 2 business days.');
        setShowNDAModal(false);
        setNdaForm({ contact_name: '', contact_email: '', company_name: '', reason: '' });
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to submit request');
      }
    } catch (error) {
      toast.error('Failed to submit request');
    } finally {
      setSubmitting(false);
    }
  };

  const handleQuestionnaireSubmit = async () => {
    if (!questionnaireFile) {
      toast.error('Please upload a questionnaire file');
      return;
    }
    setSubmitting(true);
    try {
      
      const formData = new FormData();
      formData.append('file', questionnaireFile);
      formData.append('client_name', 'Internal Upload');
      
      const response = await fetch(`${API_URL}/api/questionnaires/upload`, {
        method: 'POST',
        
        body: formData,
      });
      if (response.ok) {
        toast.success('Questionnaire uploaded! Processing will begin shortly.');
        setShowQuestionnaireModal(false);
        setQuestionnaireFile(null);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to upload questionnaire');
      }
    } catch (error) {
      toast.error('Failed to upload questionnaire');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      setQuestionnaireFile(e.dataTransfer.files[0]);
    }
  };

  // Map category icons
  const categoryIcons = {
    security: Shield,
    availability: Zap,
    processing_integrity: Settings,
    confidentiality: Lock,
    privacy: Eye,
  };

  return (
    <div className="trust-preview-page">
      <div className="page-header">
        <div>
          <h1>Trust Center Preview</h1>
          <p>This is what your clients see at the public trust center URL</p>
        </div>
        <Link to="/trust-center" target="_blank" className="btn-secondary">
          <ExternalLink size={16} /> Open Public URL
        </Link>
      </div>

      <Card className="trust-hero">
        <div className="hero-label">Trust Center</div>
        <h2>Eyvo Procurement</h2>
        <p>Enterprise eProcurement — Built on Trust. Committed to the highest standards of security, availability, and data protection.</p>
        <div className="hero-badges">
          <Badge color="#fff">SOC 2 Type II (In Preparation)</Badge>
          <Badge color="#fff">NIST 800-171</Badge>
          <Badge color="#fff">CAIQ v4.1.0</Badge>
        </div>
      </Card>

      <div className="trust-categories">
        {Object.entries(CATEGORIES).map(([k, cat]) => {
          const Icon = categoryIcons[k] || Shield;
          return (
            <Card key={k} className="trust-category-card">
              <Icon size={28} style={{ color: cat.color }} />
              <div className="trust-cat-label">{cat.label}</div>
              <Badge color={k === 'availability' || k === 'confidentiality' ? "#059669" : "#d97706"}>
                {k === 'availability' || k === 'confidentiality' ? 'Operational' : 'Monitoring'}
              </Badge>
            </Card>
          );
        })}
      </div>

      <div className="trust-actions">
        <Card>
          <h3>Request Detailed Report</h3>
          <p>Clients can submit an NDA request to access detailed compliance documentation, including CAIQ responses and security architecture details.</p>
          <button className="btn-primary" onClick={() => setShowNDAModal(true)} data-testid="request-access-btn">
            Request Access
          </button>
        </Card>
        <Card>
          <h3>Submit Security Questionnaire</h3>
          <p>Submit your security questionnaire for the Eyvo team to complete. Standard turnaround is 5 business days.</p>
          <button className="btn-secondary" onClick={() => setShowQuestionnaireModal(true)} data-testid="submit-questionnaire-btn">
            Submit Questionnaire
          </button>
        </Card>
      </div>

      {/* NDA Request Modal */}
      {showNDAModal && (
        <div className="modal-overlay" onClick={() => setShowNDAModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Request Detailed Compliance Report</h2>
              <button className="modal-close" onClick={() => setShowNDAModal(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <p style={{ marginBottom: 20, color: 'var(--text-muted)' }}>
                Submit your information below. We will send an NDA for signature and provide access to detailed compliance documentation within 2 business days.
              </p>
              <div className="form-group">
                <label>Your Name *</label>
                <input
                  type="text"
                  value={ndaForm.contact_name}
                  onChange={(e) => setNdaForm(f => ({ ...f, contact_name: e.target.value }))}
                  placeholder="John Smith"
                  data-testid="nda-name"
                />
              </div>
              <div className="form-group">
                <label>Email Address *</label>
                <input
                  type="email"
                  value={ndaForm.contact_email}
                  onChange={(e) => setNdaForm(f => ({ ...f, contact_email: e.target.value }))}
                  placeholder="john@company.com"
                  data-testid="nda-email"
                />
              </div>
              <div className="form-group">
                <label>Company Name *</label>
                <input
                  type="text"
                  value={ndaForm.company_name}
                  onChange={(e) => setNdaForm(f => ({ ...f, company_name: e.target.value }))}
                  placeholder="Acme Corporation"
                  data-testid="nda-company"
                />
              </div>
              <div className="form-group">
                <label>Reason for Request</label>
                <textarea
                  value={ndaForm.reason}
                  onChange={(e) => setNdaForm(f => ({ ...f, reason: e.target.value }))}
                  placeholder="e.g., Vendor security assessment, RFP response..."
                  rows={3}
                  data-testid="nda-reason"
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowNDAModal(false)}>Cancel</button>
              <button className="btn-primary" onClick={handleNDASubmit} disabled={submitting} data-testid="submit-nda-btn">
                {submitting ? <Loader2 className="spin" size={16} /> : null}
                {submitting ? 'Submitting...' : 'Submit Request'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Questionnaire Upload Modal */}
      {showQuestionnaireModal && (
        <div className="modal-overlay" onClick={() => setShowQuestionnaireModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Submit Security Questionnaire</h2>
              <button className="modal-close" onClick={() => setShowQuestionnaireModal(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <p style={{ marginBottom: 20, color: 'var(--text-muted)' }}>
                Upload your security questionnaire (PDF, CSV, or Excel). We will complete it using our existing compliance documentation and return within 5 business days.
              </p>
              <div
                className={`dropzone ${dragActive ? 'active' : ''} ${questionnaireFile ? 'has-file' : ''}`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                onClick={() => document.getElementById('questionnaire-file').click()}
                data-testid="questionnaire-dropzone"
              >
                <input
                  type="file"
                  id="questionnaire-file"
                  accept=".pdf,.csv,.xlsx,.xls,.json"
                  style={{ display: 'none' }}
                  onChange={(e) => setQuestionnaireFile(e.target.files[0])}
                />
                {questionnaireFile ? (
                  <>
                    <FileCheck size={32} color="#059669" />
                    <div className="dropzone-text">{questionnaireFile.name}</div>
                    <div className="dropzone-hint">Click to change file</div>
                  </>
                ) : (
                  <>
                    <Upload size={32} color="#6b7280" />
                    <div className="dropzone-text">Drag & drop your questionnaire here</div>
                    <div className="dropzone-hint">or click to browse (PDF, CSV, Excel)</div>
                  </>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowQuestionnaireModal(false)}>Cancel</button>
              <button 
                className="btn-primary" 
                onClick={handleQuestionnaireSubmit} 
                disabled={submitting || !questionnaireFile}
                data-testid="upload-questionnaire-btn"
              >
                {submitting ? <Loader2 className="spin" size={16} /> : <Upload size={16} />}
                {submitting ? 'Uploading...' : 'Upload Questionnaire'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
